import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTeachers, getSubjects } from '../services/api';
import { Skeleton } from '../components/Skeleton';
import './TeachersPage.css';

export default function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [subject,  setSubject]  = useState('الكل');
  const [search,   setSearch]   = useState('');
  const [subjects, setSubjects] = useState(['الكل']);

  // Fetch subjects
  useEffect(() => {
    getSubjects()
      .then(r => setSubjects(['الكل', ...(r.data.subjects || [])]))
      .catch(() => {});
  }, []);

  // Fetch teachers
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (subject !== 'الكل') params.subject = subject;
    getTeachers(params)
      .then(r => {
        setTeachers(r.data.teachers || []);
      })
      .catch(err => {
        console.error('Teachers fetch error:', err);
        setError('حدث خطأ في تحميل البيانات');
        setTeachers([]);
      })
      .finally(() => setLoading(false));
  }, [subject]);

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));

  const filtered = search
    ? teachers.filter(t =>
        (t.name || '').includes(search) ||
        (t.subjects || []).some(s => (s || '').includes(search))
      )
    : teachers;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="teachers-page"
    >

      {/* ── Header ── */}
      <div className="teachers-header">
        <div className="teachers-header-inner">
          <h1>المدرسون 👨‍🏫</h1>
          <p>تصفح أفضل المدرسين المستقلين واختار اللي يناسبك</p>
        </div>
      </div>

      <div className="teachers-body">

        {/* ── Filters ── */}
        <div className="teachers-filters">
          <input
            className="form-input"
            placeholder="🔍 ابحث باسم المدرّس أو المادة..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', marginBottom: 14 }}
          />
          <div className="subject-pills">
            {subjects.map(s => (
              <button
                key={s}
                className={`subject-pill ${subject === s ? 'active' : ''}`}
                onClick={() => setSubject(s)}
              >{s}</button>
            ))}
          </div>
        </div>

        {/* ── States ── */}
        {loading && (
          <div className="teachers-grid">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className="card" style={{ padding: '20px', textAlign: 'center' }}>
                <Skeleton width="80px" height="80px" borderRadius="50%" className="mb-12" style={{ margin: '0 auto 12px' }} />
                <Skeleton width="60%" height="20px" className="mb-8" style={{ margin: '0 auto 8px' }} />
                <Skeleton width="40%" height="14px" className="mb-12" style={{ margin: '0 auto 12px' }} />
                <Skeleton width="90%" height="40px" borderRadius="8px" />
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="teachers-error">
            <span style={{ fontSize: 40 }}>⚠️</span>
            <p>{error}</p>
            <button className="btn btn-outline btn-sm" onClick={() => setSubject('الكل')}>
              إعادة المحاولة
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            <div style={{ fontSize: 52 }}>🔍</div>
            <h3>{teachers.length === 0 ? 'لا يوجد مدرسون مسجلون بعد' : 'لا توجد نتائج'}</h3>
            <p>{teachers.length === 0
              ? 'كن أول مدرّس ينضم للمنصة!'
              : 'جرّب تغيير كلمة البحث أو المادة'}</p>
            {teachers.length === 0 && (
              <Link to="/register?role=teacher" className="btn btn-primary" style={{ marginTop: 16 }}>
                انضم كمدرّس
              </Link>
            )}
          </div>
        )}

        {/* ── Grid — rendered immediately, no opacity tricks ── */}
        {!loading && !error && filtered.length > 0 && (
          <div className="teachers-grid">
            {filtered.map((t, i) => (
              <Link
                key={t.uid}
                to={`/teachers/${t.username || t.uid}`}
                className="teacher-card"
                style={{ animationDelay: `${(i % 9) * 50}ms` }}
              >
                {/* Banner */}
                <div className="tc-banner">
                  {t.banner
                    ? <img src={t.banner} alt="" className="tc-banner-img" />
                    : <div className="tc-banner-ph" />}
                </div>

                {/* Avatar */}
                <div className="tc-avatar-wrap">
                  {t.avatar
                    ? <img src={t.avatar} alt={t.name} className="tc-avatar-img" />
                    : <div className="tc-avatar-ph">{t.name?.[0] || '؟'}</div>}
                </div>

                {/* Body */}
                <div className="tc-body">
                  <h3 className="tc-name">{t.name}</h3>
                  {t.title && <div className="tc-title-role" style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 600, marginBottom: 4 }}>{t.title}</div>}

                  <div className="tc-rating">
                    <span className="stars">{stars(t.rating || 0)}</span>
                    <span className="tc-reviews">({t.totalReviews || 0} تقييم)</span>
                  </div>

                  <p className="tc-bio">{t.bio || 'مدرّس متخصص على منصة مدرسك'}</p>

                  <div className="tc-subjects">
                    {(t.subjects || []).slice(0, 3).map(s => (
                      <span key={s} className="badge badge-blue" style={{ fontSize: 11 }}>{s}</span>
                    ))}
                  </div>

                  {t.pricePerHour > 0 && (
                    <div className="tc-price">{t.pricePerHour} ج.م / ساعة</div>
                  )}

                  <div className="tc-mini-stats" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 11, color: 'var(--text-muted)', justifyContent: 'center' }}>
                    <span>📚 {t.courseCount || 0} كورس</span>
                    <span>👥 {t.totalEnrolled || 0} طالب</span>
                  </div>

                  {t.socialMedia && (
                    <div className="tc-socials" style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                      {t.socialMedia.facebook && <span title="Facebook">📘</span>}
                      {t.socialMedia.instagram && <span title="Instagram">📸</span>}
                      {t.socialMedia.linkedin && <span title="LinkedIn">🔗</span>}
                      {t.socialMedia.youtube && <span title="YouTube">📺</span>}
                    </div>
                  )}
                </div>

                <div className="tc-footer">
                  <span className="tc-cta">عرض الملف الشخصي ←</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
